import { IconPopover } from '../../IconPopover/IconPopover';
import type { DefaultFormFieldLayoutProps } from '../DefaultFormFieldLayout/DefaultFormFieldLayout';

/**
 * Inherit from this base interface and switch on `type`
 * to prevent conflicting options
 */
interface BasePresentationOptions {
  position?: string;
}

export interface InlinePresentationOptions extends BasePresentationOptions {
  type: 'inline';
}

export interface TooltipPresentationOptions extends BasePresentationOptions {
  type: 'tooltip';
  isStatic?: boolean;
  iconPosition?: PopoverIconPosition;
}

export type HelperTextConfig = {
  text: string;

  // Defaults to inline
  presentation?: InlinePresentationOptions | TooltipPresentationOptions;
};

/**
 * The caller can pass a string or a HelperTextConfig to form fields.
 * If it's a string, it's displayed inline, with no custom positioning.
 */
export type HelperText = string | HelperTextConfig;

export type PopoverIconPosition = 'label-right' | 'input-right';

export interface HelperTextPopoverProps {
  text: string;

  // Is icon position static
  iconPosition?: PopoverIconPosition;
  isStatic?: boolean;
  textPosition?: string;
  instanceTestId?: string;
}

export function HelperTextPopover({
  text,
  isStatic,
  textPosition,
  instanceTestId,
}: HelperTextPopoverProps) {
  let testId = 'helper-text-popover';
  if (instanceTestId) {
    testId = `${instanceTestId}-${testId}`;
  }
  return (
    <div
      className={isStatic ? '' : 'absolute right-2 top-2'}
      data-testid={testId}
    >
      <IconPopover textPosition={textPosition}>{text}</IconPopover>
    </div>
  );
}

/**
 * Inspects the helperText for its type and properties
 * and returns props for either an inline string
 * or the HelperTextPopover as appropriate.
 */
interface HelperTextResult {
  popoverProps?: HelperTextPopoverProps;
  inlineHelperText?: string;
}

interface HelperTextPropInputs {
  helperText?: HelperText;
  label: DefaultFormFieldLayoutProps['label'];
}

export const getHelperTextProps = ({
  helperText,
  label,
}: HelperTextPropInputs): HelperTextResult => {
  if (!helperText) {
    return {};
  }

  if (typeof helperText === 'string') {
    return {
      inlineHelperText: helperText,
    };
  }

  const { presentation, text } = helperText;
  if (isTooltipPresentation(presentation)) {
    let instanceTestId: string | undefined;
    if (label && typeof label === 'string') {
      instanceTestId = label.toLowerCase().split(' ').join('-');
    }
    return {
      popoverProps: {
        text,
        iconPosition: presentation.iconPosition ?? 'label-right',
        isStatic: presentation.isStatic ?? true,
        textPosition: presentation.position,
        instanceTestId,
      },
    };
  }
  return {
    inlineHelperText: text,
  };
};

export const isTooltipPresentation = (
  presentation: HelperTextConfig['presentation']
): presentation is TooltipPresentationOptions => {
  return (
    typeof presentation === 'object' &&
    'type' in presentation &&
    presentation.type === 'tooltip'
  );
};
